﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcCore from '@mescius/wijmo';
import * as wjcNav from '@mescius/wijmo.nav';
var WjTreeViewBehavior = /** @class */ (function (_super) {
    __extends(WjTreeViewBehavior, _super);
    function WjTreeViewBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjTreeViewBehavior.tag = 'wj-tree-view';
    WjTreeViewBehavior.props = [
        'isDisabled',
        'tabOrder',
        'childItemsPath',
        'displayMemberPath',
        'imageMemberPath',
        'checkedMemberPath',
        'isContentHtml',
        'showCheckboxes',
        'autoCollapse',
        'isAnimated',
        'isReadOnly',
        'allowDragging',
        'checkOnClick',
        'expandOnClick',
        'collapseOnClick',
        'expandOnLoad',
        'lazyLoadFunction',
        'itemsSource',
        'selectedItem',
        'selectedNode',
        'checkedItems',
        'preserveSelectedNode',
        'allowMultiSelect'
    ];
    WjTreeViewBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'itemsSourceChanged',
        'loadingItems',
        'loadedItems',
        'itemClicked',
        'isCollapsedChanging',
        'isCollapsedChanged',
        'isCheckedChanging',
        'isCheckedChanged',
        'formatItem',
        'dragStart',
        'dragOver',
        'drop',
        'dragEnd',
        'nodeEditStarting',
        'nodeEditStarted',
        'nodeEditEnding',
        'nodeEditEnded'
    ];
    WjTreeViewBehavior.changeEvents = {
        'selectedItemChanged': ['selectedItem', 'selectedNode'],
        'checkedItemsChanged': ['checkedItems'],
    };
    WjTreeViewBehavior.classCtor = function () { return wjcNav.TreeView; };
    return WjTreeViewBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.nav.TreeView} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.nav.TreeView} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjTreeView = WjTreeViewBehavior.register();
function registerV3WjTreeView(app) {
    app.component(WjTreeViewBehavior.tag, WjTreeView);
}
var WjTabPanelBehavior = /** @class */ (function (_super) {
    __extends(WjTabPanelBehavior, _super);
    function WjTabPanelBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjTabPanelBehavior.prototype._createControl = function () {
        var control = new wjcNav.TabPanel(this.component.$el, null, true);
        control.beginUpdate(); // suppress updates handling during initialization
        return control;
    };
    WjTabPanelBehavior.prototype.lhMounted = function () {
        _super.prototype.lhMounted.call(this);
        this.control.onSelectedIndexChanged(); // force initial selectedTab handling
        this.control.endUpdate(); // enable updates handling
    };
    WjTabPanelBehavior.tag = 'wj-tab-panel';
    WjTabPanelBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isAnimated',
        'autoSwitch',
        'selectedIndex',
        'selectedTab'
    ];
    WjTabPanelBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjTabPanelBehavior.changeEvents = {
        'selectedIndexChanged': ['selectedIndex', 'selectedTab'],
    };
    WjTabPanelBehavior.classCtor = function () { return wjcNav.TabPanel; };
    return WjTabPanelBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.nav.TabPanel} control.
 *
 * The <b>wj-tab-panel</b> component may contain
 * a {@link wijmo.vue2.nav.WjTab} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.nav.TabPanel} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjTabPanel = WjTabPanelBehavior.register();
function registerV3WjTabPanel(app) {
    app.component(WjTabPanelBehavior.tag, WjTabPanel);
}
var WjTabBehavior = /** @class */ (function (_super) {
    __extends(WjTabBehavior, _super);
    function WjTabBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjTabBehavior.prototype._createControl = function () {
        var child = this.component.$el;
        wjcCore.assert(child.childElementCount == 2, 'TabPanel children should contain header and pane elements');
        return new wjcNav.Tab(child.children[0], child.children[1]);
    };
    WjTabBehavior.tag = 'wj-tab';
    WjTabBehavior.parentProp = 'tabs';
    WjTabBehavior.props = [
        'wjProperty',
        'isDisabled',
        'isVisible'
    ];
    WjTabBehavior.events = [
        'initialized'
    ];
    WjTabBehavior.classCtor = function () { return wjcNav.Tab; };
    return WjTabBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.nav.Tab} class.
 *
 * The <b>wj-tab</b> component should be contained in
 * a {@link wijmo.vue2.nav.WjTabPanel} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.nav.Tab} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjTab = WjTabBehavior.register();
function registerV3WjTab(app) {
    app.component(WjTabBehavior.tag, WjTab);
}
var WjAccordionBehavior = /** @class */ (function (_super) {
    __extends(WjAccordionBehavior, _super);
    function WjAccordionBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjAccordionBehavior.prototype._createControl = function () {
        var control = new wjcNav.Accordion(this.component.$el, null, true);
        control.beginUpdate(); // suppress updates handling during initialization
        return control;
    };
    WjAccordionBehavior.prototype.lhMounted = function () {
        _super.prototype.lhMounted.call(this);
        var control = this.control;
        // set default selected pane
        if ((control.selectedIndex < 0) && control.panes.length) {
            control.selectedIndex = 0;
        }
        control.endUpdate(); // enable updates handling
    };
    WjAccordionBehavior.tag = 'wj-accordion';
    WjAccordionBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isAnimated',
        'autoSwitch',
        'selectedIndex',
        'selectedPane',
        'showIcons',
        'allowCollapseAll',
        'allowExpandMany'
    ];
    WjAccordionBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjAccordionBehavior.changeEvents = {
        'selectedIndexChanged': ['selectedIndex'],
    };
    WjAccordionBehavior.classCtor = function () { return wjcNav.Accordion; };
    return WjAccordionBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.nav.Accordion} control.
 *
 * The <b>wj-accordion</b> component may contain
 * a {@link wijmo.vue2.nav.WjAccordionPane} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.nav.Accordion} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjAccordion = WjAccordionBehavior.register();
function registerV3WjAccordion(app) {
    app.component(WjAccordionBehavior.tag, WjAccordion);
}
var WjAccordionPaneBehavior = /** @class */ (function (_super) {
    __extends(WjAccordionPaneBehavior, _super);
    function WjAccordionPaneBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjAccordionPaneBehavior.prototype._createControl = function () {
        var child = this.component.$el;
        wjcCore.assert(child.childElementCount == 2, 'AccordionPane children should contain header and pane elements');
        return new wjcNav.AccordionPane(child.children[0], child.children[1]);
    };
    WjAccordionPaneBehavior.tag = 'wj-accordion-pane';
    WjAccordionPaneBehavior.parentProp = 'panes';
    WjAccordionPaneBehavior.props = [
        'wjProperty',
        'isDisabled',
        'isVisible',
        'isCollapsed'
    ];
    WjAccordionPaneBehavior.events = [
        'initialized'
    ];
    WjAccordionPaneBehavior.classCtor = function () { return wjcNav.AccordionPane; };
    return WjAccordionPaneBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.nav.AccordionPane} class.
 *
 * The <b>wj-accordion-pane</b> component should be contained in
 * a {@link wijmo.vue2.nav.WjAccordion} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.nav.AccordionPane} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjAccordionPane = WjAccordionPaneBehavior.register();
function registerV3WjAccordionPane(app) {
    app.component(WjAccordionPaneBehavior.tag, WjAccordionPane);
}
export function registerNav(app) {
    if (VueApi.isV3Plus) {
        registerV3WjTreeView(app);
        registerV3WjTabPanel(app);
        registerV3WjTab(app);
        registerV3WjAccordion(app);
        registerV3WjAccordionPane(app);
    }
}
